import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import { FaCalendarCheck, FaSpinner } from 'react-icons/fa';
import { fetchFirstSuccessful, formatShortDate, normalizeStatus, toArray } from '../utils/formExperienceApi';
import '../styles/ManageCurriculums.css';
import '../styles/ManageRanks.css';
import 'react-calendar/dist/Calendar.css';

// Real backend: GET /attendance/my-overview -> { data: { summary, submissions: [...] } }
// (see PharmD_System/routers/attendance.py get_my_attendance_overview). Each submission
// covers one or more days (multi-day/time-slot selection), so we expand it into one
// calendar entry per day.
const ATTENDANCE_ENDPOINTS = ['/attendance/my-overview'];

// There is no form-schedule-gated "open attendance forms" concept in the current
// backend (attendance submissions are always allowed, any number of times) — this
// list intentionally has no real match, so the section below always renders empty
// gracefully rather than claiming forms are open when there's nothing to gate on.
const OPEN_ATTENDANCE_ENDPOINTS = [];

const mapSubmissionToDayEntries = (submission) => {
    const days = toArray(submission?.days);
    const status = normalizeStatus(submission?.status || 'pending');
    const curriculum = submission?.curriculum_id || 'Unassigned curriculum';
    const rotationLabel = submission?.rotation_number != null ? `Rotation ${Number(submission.rotation_number) + 1}` : 'Attendance submission';

    if (!days.length) {
        return [{
            id: submission?.id || `entry-${Math.random()}`,
            date: submission?.submitted_at,
            curriculum,
            status,
            formTitle: rotationLabel,
            notes: submission?.note || ''
        }];
    }

    return days.map((day, index) => ({
        id: `${submission?.id || 'submission'}-${index}`,
        date: day?.date,
        timeSlot: day?.start_time && day?.end_time ? `${day.start_time} - ${day.end_time}` : '',
        curriculum,
        status,
        formTitle: rotationLabel,
        notes: submission?.note || ''
    }));
};

const tileClass = (status) => {
    if (status.includes('approved')) return 'mr-cal-approved';
    if (status.includes('reject')) return 'mr-cal-rejected';
    return 'mr-cal-pending';
};

const pillClass = (status) => {
    if (status.includes('approved')) return 'is-status-active';
    if (status.includes('reject')) return '';
    return 'is-status-upcoming';
};

const MyAttendancePage = () => {
    const [entries, setEntries] = useState([]);
    const [summary, setSummary] = useState(null);
    const [openForms, setOpenForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const attendancePayload = await fetchFirstSuccessful(ATTENDANCE_ENDPOINTS);
                const attendanceData = attendancePayload?.data || attendancePayload || {};
                const submissions = toArray(attendanceData?.submissions || attendanceData?.items || attendanceData);
                setEntries(submissions.flatMap(mapSubmissionToDayEntries));
                setSummary(attendanceData?.summary || null);

                if (OPEN_ATTENDANCE_ENDPOINTS.length) {
                    try {
                        const openPayload = await fetchFirstSuccessful(OPEN_ATTENDANCE_ENDPOINTS);
                        const openData = openPayload?.data || openPayload || {};
                        setOpenForms(toArray(openData?.items || openData?.forms || openData));
                    } catch (_openError) {
                        setOpenForms([]);
                    }
                }
            } catch (err) {
                setError(err?.response?.data?.message || 'Unable to load attendance');
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    const entriesByDate = useMemo(() => {
        const map = new Map();
        entries.forEach((item) => {
            if (!item.date) return;
            const key = new Date(item.date).toDateString();
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(item);
        });
        return map;
    }, [entries]);

    const selectedEntries = entriesByDate.get(selectedDate.toDateString()) || [];
    const totalSubmissions = summary?.unique_submissions ?? entries.length;
    const approvedCount = summary?.approved_count ?? entries.filter((item) => item.status.includes('approved')).length;
    const pendingCount = summary?.pending_count ?? entries.filter((item) => item.status.includes('pending') || item.status.includes('submitted')).length;
    const rejectedCount = summary?.rejected_count ?? entries.filter((item) => item.status.includes('reject')).length;

    const tileClassName = ({ date, view }) => {
        if (view !== 'month') return '';
        const values = entriesByDate.get(date.toDateString()) || [];
        if (!values.length) return '';
        if (values.some((item) => item.status.includes('approved'))) return tileClass('approved');
        if (values.some((item) => item.status.includes('reject'))) return tileClass('rejected');
        return tileClass('pending');
    };

    if (loading) {
        return (
            <div className="mc-page" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
                <FaSpinner className="mc-spin" style={{ fontSize: '3rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
            </div>
        );
    }

    return (
        <div className="mc-page">
            <div className="universal-white-container mc-shell">
                <div className="mc-hero">
                    <div className="mc-hero__copy">
                        <span className="mc-eyebrow">Attendance</span>
                        <h1>My Attendance</h1>
                        <p>Attendance forms can be submitted multiple times while the rotation remains open.</p>
                    </div>
                    <div className="mc-hero__stats">
                        <div className="mc-stat"><strong>{totalSubmissions}</strong><span>Submissions</span></div>
                        <div className="mc-stat"><strong>{approvedCount}</strong><span>Approved</span></div>
                        <div className="mc-stat"><strong>{pendingCount}</strong><span>Pending</span></div>
                        <div className="mc-stat"><strong>{rejectedCount}</strong><span>Rejected</span></div>
                    </div>
                </div>

                {error ? (
                    <div className="mc-empty-state">
                        <FaCalendarCheck />
                        <h3>Unable to load attendance</h3>
                        <p>{error}</p>
                    </div>
                ) : (
                    <>
                        <div className="mr-attendance-layout">
                            <div className="mc-form-card mr-calendar-panel">
                                <Calendar value={selectedDate} onChange={(value) => setSelectedDate(value)} tileClassName={tileClassName} />
                            </div>
                            <div className="mc-form-card mr-day-panel">
                                <h4>{selectedDate.toLocaleDateString()}</h4>
                                {selectedEntries.length === 0 && <p className="mr-hint">No attendance entries for this day.</p>}
                                {selectedEntries.map((item) => (
                                    <div key={item.id} className="mr-day-entry">
                                        <div>
                                            <strong>{item.formTitle}</strong>
                                            <p>{item.curriculum}{item.timeSlot ? ` • ${item.timeSlot}` : ''}</p>
                                            {item.notes && <small>{item.notes}</small>}
                                        </div>
                                        <span className={`mc-status-pill ${pillClass(item.status)}`}>{item.status}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {openForms.length > 0 && (
                            <div style={{ marginTop: '1.5rem' }}>
                                <h4 className="mc-subcard-title">Open Attendance Forms</h4>
                                <div className="mc-cards-grid">
                                    {openForms.map((form, index) => (
                                        <div key={form?._id || form?.id || index} className="mc-curriculum-card">
                                            <h4>{form?.title || form?.name || 'Attendance form'}</h4>
                                            <div className="mc-curriculum-card__meta">
                                                <span>Curriculum: {form?.curriculumName || form?.curriculum || '—'}</span>
                                                <span>Submissions so far: {form?.submissionCount ?? form?.submissions ?? '—'}</span>
                                                <span>Open until: {formatShortDate(form?.endDate || form?.closingDate)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
};

export default MyAttendancePage;
